import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import styles from "./inviteUserModal.module.css";
import Button from "../../../../components/button/button";
import { sendUserInvite } from "../../../../services/mantenedora";

const InviteUserModal = ({
  type,
  institution,
  institutions = [],
  mantenedoraId,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedInstitutionIds, setSelectedInstitutionIds] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const isMantenedora = type === "mantenedora";

  const mantenedoraRoles = [
    { value: "MantenedoraAdmin", label: "Admin da Mantenedora" },
    { value: "MantenedoraManager", label: "Gerente da Mantenedora" },
  ];

  const institutionRoles = [
    { value: "Admin", label: "Administrador" },
    { value: "Manager", label: "Gerente" },
    { value: "Operator", label: "Operador" },
    { value: "Financial", label: "Financeiro" },
    { value: "Academic", label: "Acadêmico" },
  ];

  const roles = isMantenedora ? mantenedoraRoles : institutionRoles;

  const handleInstitutionToggle = (institutionId) => {
    setSelectedInstitutionIds((prev) =>
      prev.includes(institutionId)
        ? prev.filter((id) => id !== institutionId)
        : [...prev, institutionId]
    );
  };

  const handleSelectAllInstitutions = () => {
    if (selectedInstitutionIds.length === institutions.length) {
      setSelectedInstitutionIds([]);
    } else {
      setSelectedInstitutionIds(institutions.map((inst) => inst.institutionId));
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const payload = {
        email: data.email.trim(),
        role: data.role,
      };

      if (isMantenedora) {
        payload.institutionIds = selectedInstitutionIds;
      } else if (institution?.id) {
        payload.institutionId = institution.id;
      }

      await sendUserInvite(mantenedoraId, payload);
      onSuccess();
    } catch (error) {
      toast.error(error.message || "Erro ao enviar convite");
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>
            {isMantenedora
              ? "Convidar por e-mail (Mantenedora)"
              : `Convidar por e-mail - ${institution?.name}`}
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <p className={styles.description}>
          Um link será enviado para o e-mail informado. O convidado poderá
          acessar o link, definir sua senha e concluir o cadastro.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <div className={styles.field}>
            <label>E-mail *</label>
            <input
              type="email"
              {...register("email", {
                required: "E-mail é obrigatório",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "E-mail inválido",
                },
              })}
              placeholder="Digite o e-mail do convidado"
            />
            {errors.email && (
              <span className={styles.error}>{errors.email.message}</span>
            )}
          </div>

          <div className={styles.field}>
            <label>Perfil *</label>
            <select
              {...register("role", { required: "Selecione um perfil" })}
              defaultValue=""
            >
              <option value="" disabled>
                Selecione um perfil
              </option>
              {roles.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
            {errors.role && (
              <span className={styles.error}>{errors.role.message}</span>
            )}
          </div>

          {isMantenedora && institutions.length > 0 && (
            <div className={`${styles.field} ${styles.institutionsSection}`}>
              <label>Acesso às Instituições</label>
              <p className={styles.institutionsHint}>
                Selecione as instituições que este usuário terá acesso.
              </p>
              <div className={styles.institutionsList}>
                <label className={styles.selectAllLabel}>
                  <input
                    type="checkbox"
                    checked={selectedInstitutionIds.length === institutions.length}
                    onChange={handleSelectAllInstitutions}
                  />
                  <span className={styles.selectAllText}>Selecionar todas</span>
                </label>
                {institutions.map((inst) => (
                  <label key={inst.institutionId} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={selectedInstitutionIds.includes(inst.institutionId)}
                      onChange={() => handleInstitutionToggle(inst.institutionId)}
                    />
                    <span className={styles.checkboxText}>
                      {inst.institutionName}
                      {inst.city && inst.state && (
                        <span className={styles.checkboxSubtext}>
                          {" "} — {inst.city}, {inst.state}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className={styles.actions}>
            <Button
              type="button"
              text="Cancelar"
              variation="outline"
              size="normal"
              onClick={onClose}
            />
            <Button
              type="submit"
              text={loading ? "Enviando..." : "Enviar convite"}
              variation="primary"
              size="normal"
              disabled={loading}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteUserModal;
